import { api } from '@/lib/api';
export type JourneyTerm = {
  id: string;
  term_number: number;
  label: string;
  status: string;
  is_current: boolean;
  starts_on: string | null;
  ends_on: string | null;
  subjects: Array<{ subject_key: string; display_name: string }>;
};
export type JourneyNode = {
  key: string;
  id: string;
  kind: 'task' | 'session' | 'assessment' | 'reflection' | 'working';
  title: string;
  term_id: string | null;
  subject_key: string | null;
  status: string;
  at: string;
  href: string;
  evidence: string;
  highest_hint_level?: number;
  due_on?: string;
  reflection?: string;
};
export type JourneyPage = {
  academic_year: {
    id: string;
    school_year: number;
    year_level: number | null;
    timezone: string;
    is_current: boolean;
  };
  terms: JourneyTerm[];
  items: JourneyNode[];
  next_cursor: string | null;
  mastery_status: 'not_assessed';
};
export const getStudyJourney = (scope: {
  academic_year_id: string;
  term_id?: string;
  subject_key?: string;
  cursor?: string;
  topic_id?: string;
}) =>
  api<JourneyPage>(
    `/study/me/journey?${new URLSearchParams(Object.entries(scope).filter(([, v]) => v !== undefined) as Array<[string, string]>)}`,
  );
export type RolloverDraft = {
  academic_year_id: string;
  from_term_id: string;
  term_number: number;
  school_year: number;
  year_level: number | null;
  subjects: Array<{ subject_key: string; display_name: string }>;
  starts_on: string | null;
  ends_on: string | null;
  tasks: Array<{ id: string; title: string; due_on: string | null }>;
  assessments: Array<{ id: string; title: string; due_on: string }>;
};
export type RolloverPlan = {
  academic_year_id: string;
  from_term_id: string;
  next_school_year?: number;
  next_year_level?: number;
  subject_keys: string[];
  starts_on: string | null;
  ends_on: string | null;
  tasks: Array<{ id: string; action: 'continue' | 'split' | 'defer' | 'archive' }>;
  assessments: Array<{ id: string; action: 'continue' | 'defer' | 'archive' }>;
  reflection: string;
};
export type RolloverPreview = {
  snapshot: string;
  from_term: string;
  to_term: string;
  school_year: number;
  task_count: number;
  assessment_count: number;
  dates: { starts_on: string | null; ends_on: string | null };
  school_deadlines_preserved: boolean;
  /** Subjects of the next Term; `added` marks one from "Add a subject" (not in this Term). */
  subjects: Array<{ subject_key: string; display_name: string; added: boolean }>;
};
export const getStudyRolloverDraft = () => api<RolloverDraft>('/study/me/rollover');
export const previewStudyRollover = (plan: RolloverPlan) =>
  api<RolloverPreview>('/study/me/rollover/preview', { method: 'POST', body: plan });
export const commitStudyRollover = (body: {
  plan: RolloverPlan;
  expected_snapshot: string;
  request_id: string;
}) =>
  api<{ to_term_id: string; academic_year_id: string }>('/study/me/rollover/commit', {
    method: 'POST',
    body,
  });

export type JourneyPortfolio = {
  version: number;
  narrative: string;
  provenance: 'learner_confirmed';
  highlights: Array<{ key: string; title: string; href: string | null; available: boolean }>;
};
export const getJourneyPortfolio = (yearId: string) =>
  api<JourneyPortfolio>(
    `/study/me/journey/portfolio?academic_year_id=${encodeURIComponent(yearId)}`,
  );
export const saveJourneyPortfolio = (body: {
  academic_year_id: string;
  expected_version: number;
  request_id: string;
  narrative: string;
  highlights: string[];
}) => api<{ version: number }>('/study/me/journey/portfolio', { method: 'POST', body });
